import { onLanguageChange, t } from '../i18n';
import { SLOTS, type Slot } from '../net/protocol';
import {
  formatAmount,
  formatMoney,
  formatMultiplier,
  fromUnits,
  normaliseStake,
  parseMultiplierToX100,
  toUnits,
} from '../util/format';

/**
 * The bet panels -- one visible by default, a second opened on request.
 *
 * The one rule they enforce locally is "do not send an obviously invalid
 * stake". Everything else -- the balance, the limits, whether the window is
 * open, which round a stake lands in -- is decided on the server, and a panel
 * only reflects the answer.
 *
 * The two panels are wholly independent: separate stake, separate auto
 * cash-out, separate auto bet, separate button, separate state machine. Nothing
 * in here couples slot 1 to slot 2, because a player who cashes one out at 1.20x
 * while the other rides to 2.20x must see two unrelated controls. The single
 * exception is the button that opens and closes the second panel, which lives on
 * BetPanels rather than in either slot.
 */

export type PanelMode =
  | 'connecting'
  | 'waiting' // nothing to do: no stake here and the window is shut
  | 'canBet' // the window is open
  | 'canPreBet' // a round is running; a stake here joins the next one
  | 'betPlaced' // staked, window still open, cancellable
  | 'preBetPlaced' // staked on the next round, cancellable until it starts
  | 'cancelSpent' // this slot's cancel allowance is used up for this round
  | 'inRound' // our stake is live and can be cashed out
  | 'cashedOut'
  | 'lost'
  | 'blocked';

export interface BetPanelHandlers {
  /** `nextRound` asks the server to place the stake on the round after this one. */
  onPlace: (
    slot: Slot,
    amount: string,
    autoCashoutX100: number | undefined,
    nextRound: boolean,
  ) => void;
  onCancel: (slot: Slot, nextRound: boolean) => void;
  onCashOut: (slot: Slot) => void;
  /** Fired when a slot's auto-bet switch is flipped by the player. */
  onAutoBetChange: (slot: Slot, on: boolean) => void;
}

/** What a mode needs to render besides the mode itself. */
interface SlotView {
  /** Already formatted for display by the caller. */
  amount?: string;
  /** The multiplier a settled bet was cashed out at. */
  cashedOutX100?: number;
}

/** The currency-dependent numbers every panel needs. */
interface PanelConfig {
  scale: number;
  minBet: string;
  maxBet: string;
  symbol: string;
  /**
   * Which account these remembered settings belong to. Two accounts sharing a
   * browser must not inherit each other's stake.
   */
  playerKey: string;
}

/** What is remembered between visits. Deliberately not the auto-bet switch. */
interface StoredSlot {
  stake?: string;
  autoTarget?: string;
  autoCashOn?: boolean;
  tab?: 'manual' | 'auto';
}

class SlotPanel {
  private readonly root: HTMLElement;
  private readonly label: HTMLElement;
  private readonly stake: HTMLInputElement;
  private readonly autocash: HTMLInputElement;
  private readonly action: HTMLButtonElement;
  private readonly quick: HTMLElement;
  private readonly autoRow: HTMLElement;
  private readonly tabs: HTMLButtonElement[];
  private readonly steps: HTMLButtonElement[];
  private readonly autoBetSwitch: HTMLButtonElement;
  private readonly autoCashSwitch: HTMLButtonElement;
  private readonly clearAuto: HTMLButtonElement;

  /**
   * The button's two text nodes, created once and never replaced.
   *
   * This is the fix for cash-out needing several clicks on a desktop browser.
   * The old panel called `replaceChildren` on the button on every animation
   * frame to update the multiplier under the label. A mouse click is a
   * mousedown and a mouseup, and a browser only synthesises `click` when both
   * land on the same element -- so a press that started on the <small> and
   * finished 16ms later, after that <small> had been thrown away and rebuilt,
   * produced no click at all. It looked like an unresponsive button; it was a
   * button whose contents were being replaced underneath the cursor sixty times
   * a second. Touch input never showed it because a tap resolves its target at
   * touchend rather than by matching two events.
   *
   * Now these two nodes live as long as the panel and only their text changes.
   * The quick-stake buttons below follow the same rule: they are built once per
   * configure(), not per render.
   */
  private readonly actionLabel = document.createElement('span');
  private readonly actionSub = document.createElement('small');

  private mode: PanelMode = 'connecting';
  private view: SlotView = {};
  private config: PanelConfig = {
    scale: 2,
    minBet: '1',
    maxBet: '1000',
    symbol: '',
    playerKey: 'anon',
  };
  private currentX100 = 100;
  private tab: 'manual' | 'auto' = 'manual';
  private autoBet = false;
  private autoCash = false;

  /**
   * Set from the instant the button is pressed until the server answers.
   *
   * The server is idempotent about a repeated cash-out -- it settles the first
   * and refuses the rest -- but a second request is still wrong: it costs a
   * round trip during the one second in this game where latency decides whether
   * the player is paid. So the button locks itself immediately, which also gives
   * the press the visible feedback whose absence made players click again.
   */
  private busy = false;

  constructor(
    readonly slot: Slot,
    private readonly handlers: BetPanelHandlers,
  ) {
    const root = document.querySelector<HTMLElement>(`.slot[data-slot="${slot}"]`);
    if (!root) throw new Error(`betpanel: no markup for slot ${slot}`);
    this.root = root;

    this.label = query(root, '.slot-label');
    this.stake = queryInput(root, 'input.stake');
    this.autocash = queryInput(root, 'input.autocash');
    this.action = queryButton(root, 'button.action');
    this.quick = query(root, '.quick');
    this.autoRow = query(root, '.slot-auto');
    this.tabs = [...root.querySelectorAll<HTMLButtonElement>('button.tab')];
    this.steps = [...root.querySelectorAll<HTMLButtonElement>('button.step')];
    this.autoBetSwitch = queryButton(root, 'button[data-switch="autobet"]');
    this.autoCashSwitch = queryButton(root, 'button[data-switch="autocash"]');
    this.clearAuto = queryButton(root, 'button.clear-auto');

    this.action.replaceChildren(this.actionLabel, this.actionSub);

    this.action.addEventListener('click', () => this.onAction());
    for (const step of this.steps) {
      step.addEventListener('click', () => this.nudge(step.dataset['step'] === 'up' ? +1 : -1));
    }

    for (const tab of this.tabs) {
      tab.addEventListener('click', () => {
        this.setTab(tab.dataset['tab'] === 'auto' ? 'auto' : 'manual');
        this.remember();
      });
    }

    this.autoBetSwitch.addEventListener('click', () => {
      if (this.autoBetSwitch.disabled) return;
      this.setAutoBet(!this.autoBet);
      // The scheduler lives outside this class; the switch only reports.
      this.handlers.onAutoBetChange(this.slot, this.autoBet);
    });

    this.autoCashSwitch.addEventListener('click', () => {
      if (this.autoCashSwitch.disabled) return;
      this.setAutoCash(!this.autoCash);
      if (this.autoCash && this.autocash.value.trim() === '') this.autocash.focus();
      this.remember();
    });

    this.clearAuto.addEventListener('click', () => {
      this.autocash.value = '';
      this.setAutoCash(false);
      this.remember();
    });

    this.autocash.addEventListener('change', () => this.remember());
    this.stake.addEventListener('change', () => this.remember());

    // Enter in either field is the same as pressing this slot's button, which is
    // what a player expects having just typed a stake into it.
    for (const el of [this.stake, this.autocash]) {
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.onAction();
      });
    }

    this.applyLabel();
    onLanguageChange(() => {
      this.applyLabel();
      this.buildQuick();
      this.render();
    });
  }

  configure(config: PanelConfig): void {
    this.config = config;

    const stored = this.readStored();
    this.stake.value = stored.stake ?? formatAmount(config.minBet, config.scale);
    this.autocash.value = stored.autoTarget ?? '';
    this.setAutoCash(stored.autoCashOn === true && this.autocash.value.trim() !== '');
    this.setTab(stored.tab ?? 'manual');

    // Auto bet always starts off, however the last session ended. A switch that
    // survives a reload is a switch that spends money on a page the player
    // opened to check their balance.
    this.setAutoBet(false);

    this.buildQuick();
    this.render();
  }

  setMode(mode: PanelMode, view: SlotView = {}): void {
    this.mode = mode;
    this.view = view;
    // A server answer of any kind ends the wait.
    this.busy = false;
    this.render();
  }

  /** True while this slot holds a stake that can still be cashed out. */
  get isLive(): boolean {
    return this.mode === 'inRound';
  }

  /** True while this slot holds any stake the player has not got back yet. */
  get hasStake(): boolean {
    return (
      this.mode === 'betPlaced' ||
      this.mode === 'preBetPlaced' ||
      this.mode === 'inRound' ||
      this.busy
    );
  }

  get autoBetOn(): boolean {
    return this.autoBet;
  }

  /** The stake as it would be sent right now, or null if it is unusable. */
  currentStake(): string | null {
    return normaliseStake(this.stake.value, this.config.scale);
  }

  /** The auto cash-out target as it would be sent right now. */
  currentAutoCashout(): number | undefined {
    if (!this.autoCash) return undefined;
    const parsed = parseMultiplierToX100(this.autocash.value);
    return parsed > 100 ? parsed : undefined;
  }

  /** Puts the switch back where the server's state says it should be. */
  setAutoBet(on: boolean): void {
    this.autoBet = on;
    this.autoBetSwitch.setAttribute('aria-checked', on ? 'true' : 'false');
  }

  /**
   * Restores an auto cash-out target the server is already holding, so a
   * reconnect mid-round shows the target the live bet actually carries rather
   * than an empty field.
   */
  showServerAutoCashout(x100: number | undefined): void {
    if (!x100 || x100 <= 100) return;
    this.autocash.value = formatMultiplier(x100).replace('x', '');
    this.setAutoCash(true);
    this.setTab('auto');
  }

  setMultiplier(x100: number): void {
    this.currentX100 = x100;
    // Only the one string that changed, and only when it is on screen. The
    // button's structure is never touched here; see actionLabel.
    if (this.mode === 'inRound' && !this.busy) {
      setText(this.actionSub, formatMultiplier(x100));
    }
  }

  /** Presses the button from outside, for the cash-out keyboard shortcut. */
  activate(): void {
    this.onAction();
  }

  /**
   * Places this slot's stake without a press, for the auto-bet scheduler.
   *
   * It goes through the same path as a press -- same validation, same busy lock,
   * same handler -- so an automated stake cannot take a shortcut a manual one
   * does not have.
   */
  autoPlace(nextRound: boolean): boolean {
    if (this.busy) return false;
    if (nextRound ? this.mode !== 'canPreBet' : this.mode !== 'canBet') return false;

    const amount = this.currentStake();
    if (!amount) return false;

    this.markBusy();
    this.handlers.onPlace(this.slot, amount, this.currentAutoCashout(), nextRound);
    return true;
  }

  private setTab(tab: 'manual' | 'auto'): void {
    this.tab = tab;
    for (const el of this.tabs) {
      const isThis = (el.dataset['tab'] === 'auto' ? 'auto' : 'manual') === tab;
      el.setAttribute('aria-selected', isThis ? 'true' : 'false');
    }
    if (tab === 'auto') this.autoRow.removeAttribute('hidden');
    else this.autoRow.setAttribute('hidden', '');
  }

  private setAutoCash(on: boolean): void {
    this.autoCash = on;
    this.autoCashSwitch.setAttribute('aria-checked', on ? 'true' : 'false');
    this.render();
  }

  /**
   * Builds the quick-stake buttons for this currency.
   *
   * The multiples are the minimum times one, two, five and ten rather than a
   * fixed 1/2/5/10, because those numbers only look right in a currency whose
   * minimum is 1. In USD the minimum is 0.10 and in BTC it is 0.000001; a
   * hard-coded "5" would be fifty times the maximum in one and unreachable in
   * the other.
   */
  private buildQuick(): void {
    const { scale, minBet, maxBet } = this.config;
    const minUnits = toUnits(minBet, scale);
    const maxUnits = toUnits(maxBet, scale);
    if (minUnits === null || minUnits <= 0) {
      this.quick.replaceChildren();
      return;
    }

    const seen = new Set<number>();
    const values: number[] = [];
    for (const factor of [1, 2, 5, 10]) {
      const units = minUnits * factor;
      if (maxUnits !== null && units > maxUnits) continue;
      if (seen.has(units)) continue;
      seen.add(units);
      values.push(units);
    }

    this.quick.replaceChildren(
      ...values.map((units) => {
        const value = fromUnits(units, scale);
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = formatAmount(value, scale);
        button.setAttribute(
          'aria-label',
          t('panel.quickAmount', { amount: formatMoney(value, scale, this.config.symbol) }),
        );
        button.addEventListener('click', () => {
          this.stake.value = formatAmount(value, scale);
          this.remember();
        });
        return button;
      }),
    );
  }

  private applyLabel(): void {
    this.label.textContent = t('panel.slot', { n: this.slot });
  }

  private onAction(): void {
    if (this.busy || this.action.disabled) return;

    switch (this.mode) {
      case 'canBet':
      case 'canPreBet': {
        const amount = this.currentStake();
        if (!amount) {
          this.flashInvalid(this.stake);
          return;
        }
        this.stake.value = amount;

        let autoCashoutX100: number | undefined;
        if (this.autoCash) {
          const parsed = parseMultiplierToX100(this.autocash.value);
          // The server refuses anything at or below 1.00x with its own error
          // code; refusing it here as well saves the round trip and points at
          // the field that is wrong.
          if (parsed <= 100) {
            this.flashInvalid(this.autocash);
            return;
          }
          autoCashoutX100 = parsed;
          this.autocash.value = formatMultiplier(parsed).replace('x', '');
        }

        this.remember();
        this.markBusy();
        this.handlers.onPlace(this.slot, amount, autoCashoutX100, this.mode === 'canPreBet');
        break;
      }

      case 'betPlaced':
      case 'preBetPlaced':
        this.markBusy();
        this.handlers.onCancel(this.slot, this.mode === 'preBetPlaced');
        break;

      case 'inRound':
        this.markBusy();
        this.handlers.onCashOut(this.slot);
        break;

      default:
        break;
    }
  }

  /**
   * Locks the button and says so, before the request has even left.
   *
   * If the answer never arrives the button stays locked, which is correct: the
   * socket's own reconnect will bring a fresh snapshot and that calls setMode.
   */
  private markBusy(): void {
    this.busy = true;
    this.action.disabled = true;
    this.action.classList.add('pending');
    setText(this.actionLabel, t('panel.sending'));
    setText(this.actionSub, '');
  }

  private nudge(direction: 1 | -1): void {
    const { scale, minBet, maxBet } = this.config;
    const current = toUnits(this.currentStake() ?? minBet, scale);
    if (current === null) return;

    // Doubling and halving, which is how players actually adjust a crash stake.
    const next = direction > 0 ? current * 2 : Math.floor(current / 2);
    const min = toUnits(minBet, scale) ?? 1;
    const max = toUnits(maxBet, scale);

    // Clamped rather than left out of range: halving twice used to walk the
    // field below the minimum, and the only sign of it was the server's refusal
    // once the player pressed bet.
    let clamped = Math.max(min, next);
    if (max !== null) clamped = Math.min(max, clamped);

    this.stake.value = formatAmount(fromUnits(clamped, scale), scale);
    this.remember();
  }

  private render(): void {
    const { action, stake, autocash } = this;

    action.classList.remove('cashout', 'cancel', 'pending');
    action.disabled = false;
    stake.disabled = false;
    this.root.classList.toggle('live', this.mode === 'inRound');

    let label = '';
    let sub = '';
    let locked = false;
    // While a stake is in play its amount and target are the server's, not the
    // field's, so the inputs are read-only until it settles.
    let inputsLocked = false;

    switch (this.mode) {
      case 'connecting':
        label = t('panel.connecting');
        locked = true;
        break;

      case 'waiting':
        label = t('panel.waiting');
        locked = true;
        break;

      case 'canBet':
        label = t('panel.bet');
        break;

      case 'cancelSpent':
        // The window may still be open and the other slot may still be usable,
        // so this says which thing is closed and when it comes back. The stake
        // field stays editable on purpose: the player can line up the next round.
        label = t('panel.cancelSpent');
        sub = t('panel.cancelSpentNote');
        locked = true;
        break;

      case 'canPreBet':
        // Same action, different round. Saying so on the button is the only
        // warning a player gets that this stake is not for the round on screen.
        label = t('panel.bet');
        sub = t('panel.nextRoundNote');
        break;

      case 'betPlaced':
        action.classList.add('cancel');
        label = t('panel.cancel');
        sub = t('panel.staked', { amount: this.view.amount ?? '' });
        inputsLocked = true;
        break;

      case 'preBetPlaced':
        action.classList.add('cancel');
        label = t('panel.cancel');
        sub = t('panel.queued', { amount: this.view.amount ?? '' });
        inputsLocked = true;
        break;

      case 'inRound':
        action.classList.add('cashout');
        label = t('panel.cashOut');
        sub = formatMultiplier(this.currentX100);
        inputsLocked = true;
        break;

      case 'cashedOut':
        label = t('panel.cashedOut', {
          multiplier: formatMultiplier(this.view.cashedOutX100 ?? this.currentX100),
        });
        locked = true;
        inputsLocked = true;
        break;

      case 'lost':
        label = t('panel.lost');
        locked = true;
        inputsLocked = true;
        break;

      case 'blocked':
        label = t('panel.blocked');
        locked = true;
        inputsLocked = true;
        break;
    }

    action.disabled = locked;
    stake.disabled = inputsLocked;
    for (const step of this.steps) step.disabled = inputsLocked;
    for (const button of this.quick.querySelectorAll('button')) button.disabled = inputsLocked;

    // The target may not be edited while the bet it belongs to is live: the
    // server fixed it when the stake was taken and there is no message that
    // changes it.
    autocash.disabled = inputsLocked || !this.autoCash;
    this.autoCashSwitch.disabled = inputsLocked;
    this.autoBetSwitch.disabled = this.mode === 'blocked';

    setText(this.actionLabel, label);
    setText(this.actionSub, sub);
  }

  private flashInvalid(el: HTMLInputElement): void {
    const original = el.style.borderColor;
    el.style.borderColor = 'var(--red)';
    el.focus();
    window.setTimeout(() => {
      el.style.borderColor = original;
    }, 700);
  }

  // ----- remembered settings -------------------------------------------------

  private get storageKey(): string {
    return `fighter.slot.${this.config.playerKey}.${this.slot}`;
  }

  private readStored(): StoredSlot {
    try {
      const raw = window.localStorage.getItem(this.storageKey);
      if (!raw) return {};
      const parsed: unknown = JSON.parse(raw);
      return typeof parsed === 'object' && parsed !== null ? (parsed as StoredSlot) : {};
    } catch {
      // Private browsing can make localStorage throw on access, and a stake the
      // player has to retype is a smaller problem than a game that will not open.
      return {};
    }
  }

  private remember(): void {
    const stored: StoredSlot = {
      stake: this.stake.value,
      autoTarget: this.autocash.value,
      autoCashOn: this.autoCash,
      tab: this.tab,
    };
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(stored));
    } catch {
      /* see readStored */
    }
  }
}

/**
 * Both panels, the second one's visibility, and the keyboard shortcut they
 * share.
 *
 * The shortcut lives here rather than in SlotPanel because there is one space
 * bar and two slots: two panels each listening on the window would both fire on
 * one keypress and cash out both bets, which is the opposite of independent.
 */
export class BetPanels {
  private readonly panels: SlotPanel[];
  private readonly footer = must('betpanel');
  private readonly openButton = mustButton('slot2-open');
  private readonly closeButton = mustButton('slot2-close');

  private secondOpen = false;
  private playerKey = 'anon';

  constructor(handlers: BetPanelHandlers) {
    // The slots get a wrapped copy so that a switch the *player* flips also
    // refreshes the close lock. Without this the lock only tracked stakes: auto
    // bet could be running in the second panel while its close button sat
    // enabled, and closing it would have hidden a panel that was still betting.
    const wrapped: BetPanelHandlers = {
      ...handlers,
      onAutoBetChange: (slot, on) => {
        handlers.onAutoBetChange(slot, on);
        this.refreshCloseLock();
      },
    };
    this.panels = SLOTS.map((slot) => new SlotPanel(slot, wrapped));

    this.openButton.addEventListener('click', () => this.setSecondOpen(true));
    this.closeButton.addEventListener('click', () => {
      // The button is disabled while the slot holds anything, so this is the
      // ordinary path; the guard is here because a disabled button can still be
      // reached by a script and the rule belongs with the state, not the CSS.
      if (this.closeButton.disabled) return;
      this.setSecondOpen(false);
    });

    // The space bar is the fastest thing a hand can reach, and cashing out is
    // the only moment in this game where a few hundred milliseconds matter. It
    // takes the first live slot: with two stakes running, the lower one is the
    // one a player is watching for the early exit.
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'Space' || e.repeat) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLSelectElement) return;
      // A focused button already acts on the space bar. Handling it here as well
      // would press two different slots from one keystroke.
      if (e.target instanceof HTMLButtonElement) return;

      const live = this.panels.find((p) => p.isLive);
      if (!live) return;
      e.preventDefault();
      live.activate();
    });

    this.applySecondVisibility();
  }

  configure(config: Omit<PanelConfig, 'playerKey'> & { playerKey: string }): void {
    this.playerKey = config.playerKey;
    for (const p of this.panels) p.configure(config);

    // The player's own choice about the second panel, remembered per account.
    this.setSecondOpen(this.readSecondOpen(), { remember: false });
  }

  setMode(slot: Slot, mode: PanelMode, view?: SlotView): void {
    this.panel(slot).setMode(mode, view);
    this.refreshCloseLock();
  }

  /** Applies one mode to both slots, for the states that are never per-slot. */
  setAll(mode: PanelMode): void {
    for (const p of this.panels) p.setMode(mode);
    this.refreshCloseLock();
  }

  setMultiplier(x100: number): void {
    for (const p of this.panels) p.setMultiplier(x100);
  }

  panelFor(slot: Slot): {
    autoBetOn: boolean;
    hasStake: boolean;
    autoPlace: (nextRound: boolean) => boolean;
    setAutoBet: (on: boolean) => void;
    showServerAutoCashout: (x100: number | undefined) => void;
  } {
    const p = this.panel(slot);
    return {
      get autoBetOn() {
        return p.autoBetOn;
      },
      get hasStake() {
        return p.hasStake;
      },
      autoPlace: (nextRound) => p.autoPlace(nextRound),
      setAutoBet: (on) => {
        p.setAutoBet(on);
        this.refreshCloseLock();
      },
      showServerAutoCashout: (x100) => p.showServerAutoCashout(x100),
    };
  }

  /**
   * Makes sure a slot the server says we hold is on screen.
   *
   * Without this, a stake placed on one device and recovered by a snapshot on
   * another would exist with no panel to cash it out from -- the second panel
   * would be closed and the cash-out button unreachable. Server state wins over
   * the remembered preference, always.
   */
  ensureVisible(slot: Slot): void {
    if (slot === 2 && !this.secondOpen) this.setSecondOpen(true, { remember: false });
  }

  private setSecondOpen(open: boolean, opts: { remember?: boolean } = {}): void {
    this.secondOpen = open;
    this.applySecondVisibility();
    if (opts.remember !== false) this.writeSecondOpen(open);
  }

  private applySecondVisibility(): void {
    const second = document.querySelector<HTMLElement>('.slot[data-slot="2"]');
    if (second) {
      if (this.secondOpen) second.removeAttribute('hidden');
      else second.setAttribute('hidden', '');
    }
    this.footer.classList.toggle('single', !this.secondOpen);
    if (this.secondOpen) this.openButton.setAttribute('hidden', '');
    else this.openButton.removeAttribute('hidden');
    this.refreshCloseLock();
  }

  /**
   * Disables the close button, with a reason, whenever closing would hide
   * something that is still running.
   *
   * There is no server call and no error code here on purpose: the second panel
   * is a view, not a bet, and the server neither knows nor needs to know whether
   * it is on screen. What the server does know is the stake -- which is why the
   * lock is derived from the slot's mode rather than from a local flag.
   */
  private refreshCloseLock(): void {
    if (!this.secondOpen) return;
    const second = this.panel(2);

    let reasonKey: 'panel.closeBlockedActiveBet' | 'panel.closeBlockedAutoBet' | null = null;
    if (second.hasStake) reasonKey = 'panel.closeBlockedActiveBet';
    else if (second.autoBetOn) reasonKey = 'panel.closeBlockedAutoBet';

    this.closeButton.disabled = reasonKey !== null;
    this.closeButton.title = reasonKey ? t(reasonKey) : t('panel.closeSecond');
  }

  private get secondKey(): string {
    return `fighter.slot2Open.${this.playerKey}`;
  }

  private readSecondOpen(): boolean {
    try {
      return window.localStorage.getItem(this.secondKey) === '1';
    } catch {
      return false;
    }
  }

  private writeSecondOpen(open: boolean): void {
    try {
      window.localStorage.setItem(this.secondKey, open ? '1' : '0');
    } catch {
      /* the preference simply will not survive a reload */
    }
  }

  private panel(slot: Slot): SlotPanel {
    const found = this.panels.find((p) => p.slot === slot);
    if (!found) throw new Error(`betpanel: slot ${slot} is not one of ours`);
    return found;
  }
}

/** Writes only when the text actually differs, to keep the frame loop cheap. */
function setText(el: HTMLElement, text: string): void {
  if (el.textContent !== text) el.textContent = text;
}

function query(root: HTMLElement, selector: string): HTMLElement {
  const el = root.querySelector<HTMLElement>(selector);
  if (!el) throw new Error(`betpanel: ${selector} is missing`);
  return el;
}

function queryInput(root: HTMLElement, selector: string): HTMLInputElement {
  const el = root.querySelector(selector);
  if (!(el instanceof HTMLInputElement)) throw new Error(`betpanel: ${selector} is not an input`);
  return el;
}

function queryButton(root: HTMLElement, selector: string): HTMLButtonElement {
  const el = root.querySelector(selector);
  if (!(el instanceof HTMLButtonElement)) throw new Error(`betpanel: ${selector} is not a button`);
  return el;
}

function must(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`betpanel: #${id} is missing from the page`);
  return el;
}

function mustButton(id: string): HTMLButtonElement {
  const el = document.getElementById(id);
  if (!(el instanceof HTMLButtonElement)) throw new Error(`betpanel: #${id} is not a button`);
  return el;
}
